import { useMutation } from "react-query";
import {
  createDocumentRule,
  updateDocumentRule,
  deleteDocumentRule,
  DocumentRulePayload,
} from "api/insurancePlanDocumentRules";
import { useGlobalToast } from "components/GlobalToastProvider";

export const useCreateDocumentRule = (options: Record<string, any> = {}) => {
  const { onError = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();

  const { mutate, isLoading, ...rest } = useMutation(
    ({ planId, payload }: { planId: number; payload: DocumentRulePayload }) =>
      createDocumentRule(planId, payload),
    {
      onError: (error, variables, context) => {
        showToast({
          message: "Error creating document rule",
          errorState: true,
        });
        onError(error, variables, context);
      },
      ...otherOptions,
    }
  );

  return { createDocumentRuleMutation: mutate, isCreateDocumentRuleLoading: isLoading, ...rest };
};

export const useUpdateDocumentRule = (options: Record<string, any> = {}) => {
  const { onError = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();

  const { mutate, isLoading, ...rest } = useMutation(
    ({
      planId,
      ruleId,
      payload,
    }: {
      planId: number;
      ruleId: number;
      payload: Partial<DocumentRulePayload>;
    }) => updateDocumentRule(planId, ruleId, payload),
    {
      onError: (error, variables, context) => {
        showToast({
          message: "Error updating document rule",
          errorState: true,
          retryHandler: () => mutate(variables),
        });
        onError(error, variables, context);
      },
      ...otherOptions,
    }
  );

  return { updateDocumentRuleMutation: mutate, isUpdateDocumentRuleLoading: isLoading, ...rest };
};

export const useDeleteDocumentRule = (options: Record<string, any> = {}) => {
  const { onError = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();

  const { mutate, isLoading, ...rest } = useMutation(
    ({ planId, ruleId }: { planId: number; ruleId: number }) => deleteDocumentRule(planId, ruleId),
    {
      onError: (error, variables, context) => {
        showToast({
          message: "Error deleting document rule",
          errorState: true,
          retryHandler: () => mutate(variables),
        });
        onError(error, variables, context);
      },
      ...otherOptions,
    }
  );

  return { deleteDocumentRuleMutation: mutate, isDeleteDocumentRuleLoading: isLoading, ...rest };
};
